import React, { Fragment, useMemo } from 'react';
import { ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { Text } from '@/components/gluestack/text';
import { HStack } from '~/components/gluestack/hstack';
import { VStack } from '~/components/gluestack/vstack';
import { Card } from '~/components/gluestack/card';
import { Divider } from '@/components/gluestack/divider';
import { Button, ButtonText } from '@/components/gluestack/button';
import { MenuSelectButton } from '@/components/ui/MenuSelectButton';
import { MessageView } from '@/components/ui/MessageView';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import { useCommunity } from '@/hooks/useCommunity';
import { useOpenWebPage } from '@/hooks/useOpenWebPage';
import {
  useNotificationSound,
  type NotificationSoundKind,
} from '@/hooks/useNotificationSound';
import {
  NOTIFICATION_SOUNDS,
  NOTIFICATION_SOUND_OFF,
  type NotificationSoundId,
} from '@/constants/notificationSounds';
import { APP_STORE_URLS } from '@/constants/urls';
import {
  MessageMultiple01Icon,
  Notification02Icon,
  PlayIcon,
} from '@hugeicons-pro/core-stroke-rounded';
import {
  AppStoreIcon,
  PlayStoreIcon,
} from '@hugeicons-pro/core-solid-rounded';

interface ISoundOption {
  key: string;
  label: string;
}

interface ISoundRowProps {
  icon: any;
  label: string;
  selectedId: NotificationSoundId;
  options: ISoundOption[];
  onSelect: (id: NotificationSoundId) => void;
  onPreview: () => void;
  previewAriaLabel: string;
}

function SoundRow({
  icon,
  label,
  selectedId,
  options,
  onSelect,
  onPreview,
  previewAriaLabel,
}: ISoundRowProps) {
  const selectedLabel = options.find((o) => o.key === selectedId)?.label ?? '';
  const previewDisabled = selectedId === NOTIFICATION_SOUND_OFF;

  return (
    <HStack className="items-center justify-between gap-3 py-2">
      <HStack className="min-w-0 flex-shrink items-center gap-2">
        <HugeIconsWrapper icon={icon} size={22} className="color-typography-500" />
        <Text className="font-semibold" isTruncated>
          {label}
        </Text>
      </HStack>
      <HStack className="items-center gap-2">
        <MenuSelectButton
          selectedKey={selectedId}
          valueLabel={selectedLabel}
          options={options}
          onSelect={(key) => onSelect(key as NotificationSoundId)}
          placement="bottom right"
          offset={8}
          size="sm"
          className="gap-2"
          iconClassName="color-white"
        />
        <Button
          size="sm"
          variant="solid"
          action="neutral"
          onPress={onPreview}
          isDisabled={previewDisabled}
          accessibilityLabel={previewAriaLabel}
        >
          <HugeIconsWrapper icon={PlayIcon} size={16} className="color-typography-700" />
        </Button>
      </HStack>
    </HStack>
  );
}

export default function NotificationsWebSettings() {
  const { t } = useTranslation();
  const isLargeScreen = useIsLargeScreen();
  const community = useCommunity();
  const openWebPage = useOpenWebPage();
  const {
    messageSoundId,
    notificationSoundId,
    setSound,
    previewSound,
  } = useNotificationSound();

  const options = useMemo<ISoundOption[]>(
    () => [
      { key: NOTIFICATION_SOUND_OFF, label: t('settings.notifications.sounds.off') },
      ...NOTIFICATION_SOUNDS.map((s) => ({ key: s.id, label: t(s.labelKey) })),
    ],
    [t]
  );

  const previewAriaLabel = t('settings.notifications.web.preview');

  const rows: { kind: NotificationSoundKind; icon: any; label: string; selectedId: NotificationSoundId }[] = [
    {
      kind: 'message',
      icon: MessageMultiple01Icon,
      label: t('settings.notifications.web.messageSoundLabel'),
      selectedId: messageSoundId,
    },
    {
      kind: 'notification',
      icon: Notification02Icon,
      label: t('settings.notifications.web.notificationSoundLabel'),
      selectedId: notificationSoundId,
    },
  ];

  return (
    <>
      <ScreenHeader
        options={{
          title: t(isLargeScreen ? 'settings.notifications.titleWithSounds' : 'settings.notifications.title'),
        }}
      />
      <ScrollView contentContainerStyle={{ flexGrow: 1 }}>
        <VStack className="mx-auto w-full max-w-4xl flex-1 p-3 md:pt-0" space="md">
          {isLargeScreen ? (
            <>
              <Text size="sm" className="mx-4 text-typography-600">
                {t('settings.notifications.web.soundsDescription', { communityName: community?.name ?? '' })}
              </Text>

              <Card className="gap-3" size="md">
                {rows.map((row, idx) => (
                  <Fragment key={row.kind}>
                    {idx > 0 && <Divider className="-my-2" />}
                    <SoundRow
                      icon={row.icon}
                      label={row.label}
                      selectedId={row.selectedId}
                      options={options}
                      onSelect={(id) => setSound(row.kind, id)}
                      onPreview={() => previewSound(row.selectedId)}
                      previewAriaLabel={previewAriaLabel}
                    />
                  </Fragment>
                ))}
              </Card>
            </>
          ) : (
            <View className="flex-1 items-center justify-center px-8">
              <MessageView
                type="info"
                icon={Notification02Icon}
                text={t('settings.notifications.web.mobileTitle')}
                subText={t('settings.notifications.web.mobileSubtitle')}
                disableHeight
              >
                <VStack space="sm" className="w-full max-w-xs pt-2 gap-4">
                  <Button
                    variant="solid"
                    action="primary"
                    onPress={() => openWebPage(APP_STORE_URLS.ios)}
                  >
                    <HugeIconsWrapper icon={AppStoreIcon} size={18} className="color-white" />
                    <ButtonText>{t('settings.notifications.web.appStoreButton')}</ButtonText>
                  </Button>
                  <Button
                    variant="solid"
                    action="primary"
                    onPress={() => openWebPage(APP_STORE_URLS.android)}
                  >
                    <HugeIconsWrapper icon={PlayStoreIcon} size={18} className="color-white" />
                    <ButtonText>{t('settings.notifications.web.googlePlayButton')}</ButtonText>
                  </Button>
                </VStack>
              </MessageView>
            </View>
          )}
        </VStack>
      </ScrollView>
    </>
  );
}
